import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const adminDoc = await getDoc(doc(db, 'admins', firebaseUser.uid));
      if (!adminDoc.exists()) {
        setAuthError('No tienes permisos de administración.');
        await signOut(auth);
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Force-refresh so Storage rules see request.auth.token.admin
      const token = await firebaseUser.getIdTokenResult(true);
      if (token.claims.admin !== true) {
        setAuthError(
          'Tu cuenta es admin en Firestore, pero falta el claim de Auth. Ejecuta: npm run admin:claim -- --uid ' +
            firebaseUser.uid
        );
        await signOut(auth);
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);
      setIsAdmin(true);
      setAuthError('');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = (email, password) => {
    setAuthError('');
    return signInWithEmailAndPassword(auth, email, password);
  };
  const logout = () => signOut(auth);

  const value = useMemo(
    () => ({
      user,
      isAdmin,
      loading,
      login,
      logout,
      authError
    }),
    [user, isAdmin, loading, authError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
